/**
 * The Effort_Assumption editor (task 13.2, R2.1, R2.2, R2.5, R2.6, R7.8).
 *
 * ## Every submission is a new version
 *
 * R2.2: an Effort_Assumption is never edited in place. Opening this dialog from an
 * existing row pre-fills a *new version* of that combination rather than loading a
 * record for mutation, and the effective date defaults to today rather than to the
 * previous version's - it is the date this figure starts applying from.
 *
 * That versioning is what makes R8.3's historical reproduction possible: a demand
 * figure calculated last month resolves against whichever version was effective
 * then, so superseding an assumption never rewrites a past calculation.
 *
 * ## The mandatory recalculation warning is the server's, not ours
 *
 * R2.5: a submission that would trigger recalculation is refused until the writer
 * acknowledges it. The dialog submits without an acknowledgement first and lets the
 * service decide; when the answer is a `validation_failed` carrying warning
 * findings, it shows them and asks for confirmation, then re-submits echoing back
 * the exact `ruleId`s the server named. The client never decides that a warning
 * applies and never hardcodes `EA-RECALC` - which also keeps it clear of the API
 * package it cannot import.
 *
 * ## Anatomy
 *
 * {@link EditorDialog} — the shared editor-popup shape of UI spec §3.11: one card,
 * a 13px heading with an 11px note under it, then this module's own content. All
 * that belongs here is the findings, the field grid and the action row.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EffortAssumption, EffortAssumptionWrite, Finding } from '@complan/contracts';
import {
  EFFORT_PER_UNIT_MINUTES_MAX,
  EFFORT_PER_UNIT_MINUTES_MIN,
  FIXED_OVERHEAD_HOURS_MAX,
  FIXED_OVERHEAD_HOURS_MIN,
} from '@complan/contracts';
import type { FieldForceRole } from '@complan/domain';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Field, FORM_CONTROL_CLASS, TextInput } from '@/features/initiative/intake/fields';
import { DateField } from '@/shared/components/DateField';
import { EditorDialog, EditorDialogActions } from '@/shared/components/EditorDialog';
import { cn } from '@/lib/utils';
import { FindingList } from '../shared/FindingList';
import { FF_ROLE_OPTIONS } from '../ff-role-options';

/** The shared form control, filling its grid cell — as in the Capacity Inputs editor. */
const CONTROL_CLASS = cn(FORM_CONTROL_CLASS, 'w-full');

/** A master data option the two combination selects offer. */
export interface CombinationOption {
  readonly id: string;
  readonly name: string;
}

/** The dialog's own form state; every field is a string so inputs stay controlled. */
interface FormState {
  activityTypeId: string;
  ffRole: string;
  populationUnitId: string;
  effortPerUnitMinutes: string;
  /** Blank means "no fixed overhead", which the contract carries as `null`. */
  fixedOverheadHours: string;
  effectiveFrom: string;
}

export interface EffortAssumptionDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /**
   * The version whose combination and figures seed the form, or `null` for a blank
   * entry. Never mutated - see this module's note on R2.2.
   */
  readonly basedOn: EffortAssumption | null;
  readonly activityTypes: readonly CombinationOption[];
  readonly populationUnits: readonly CombinationOption[];
  readonly defaultFfRole: FieldForceRole;
  readonly onSubmit: (payload: EffortAssumptionWrite) => void;
  readonly isSubmitting: boolean;
  /** Findings the server returned for the last rejected submit. */
  readonly findings: readonly Finding[];
  readonly onCloseAutoFocus?: (event: Event) => void;
}

const today = (): string => new Date().toISOString().slice(0, 10);

function seedState(
  basedOn: EffortAssumption | null,
  activityTypes: readonly CombinationOption[],
  populationUnits: readonly CombinationOption[],
  defaultFfRole: FieldForceRole,
): FormState {
  return {
    activityTypeId: basedOn?.activityTypeId ?? activityTypes[0]?.id ?? '',
    ffRole: basedOn?.ffRole ?? defaultFfRole,
    populationUnitId: basedOn?.populationUnitId ?? populationUnits[0]?.id ?? '',
    effortPerUnitMinutes: basedOn?.effortPerUnitMinutes ?? '',
    fixedOverheadHours: basedOn?.fixedOverheadHours ?? '',
    // Today, not the previous version's date: this is when the new figure starts
    // applying, and back-dating it would silently change past calculations.
    effectiveFrom: today(),
  };
}

export function EffortAssumptionDialog({
  open,
  onOpenChange,
  basedOn,
  activityTypes,
  populationUnits,
  defaultFfRole,
  onSubmit,
  isSubmitting,
  findings,
  onCloseAutoFocus,
}: EffortAssumptionDialogProps): React.JSX.Element {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(() =>
    seedState(basedOn, activityTypes, populationUnits, defaultFfRole),
  );

  // Reseed on open, so a second open does not show the previous entry's values.
  useEffect(() => {
    if (open) {
      setForm(seedState(basedOn, activityTypes, populationUnits, defaultFfRole));
    }
  }, [open, basedOn, activityTypes, populationUnits, defaultFfRole]);

  const warnings = useMemo(
    () => findings.filter((finding) => finding.tier === 'warning'),
    [findings],
  );
  const blocking = useMemo(
    () => findings.filter((finding) => finding.tier !== 'warning'),
    [findings],
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const effort = Number(form.effortPerUnitMinutes);
  const effortOutOfRange =
    form.effortPerUnitMinutes !== '' &&
    (!Number.isFinite(effort) ||
      effort < EFFORT_PER_UNIT_MINUTES_MIN ||
      effort > EFFORT_PER_UNIT_MINUTES_MAX);

  const complete =
    form.activityTypeId !== '' &&
    form.populationUnitId !== '' &&
    form.effortPerUnitMinutes !== '' &&
    form.effectiveFrom !== '';

  const submittable = complete && !effortOutOfRange && !isSubmitting;

  const submit = (acknowledgedWarningRuleIds: readonly string[]): void => {
    onSubmit({
      activityTypeId: form.activityTypeId,
      ffRole: form.ffRole as FieldForceRole,
      populationUnitId: form.populationUnitId,
      effortPerUnitMinutes: toDecimalText(form.effortPerUnitMinutes),
      fixedOverheadHours:
        form.fixedOverheadHours.trim() === '' ? null : toDecimalText(form.fixedOverheadHours),
      effectiveFrom: form.effectiveFrom as EffortAssumptionWrite['effectiveFrom'],
      acknowledgedWarningRuleIds: [...acknowledgedWarningRuleIds],
    } as EffortAssumptionWrite);
  };

  return (
    <EditorDialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        basedOn === null
          ? t('manpower.effortMatrix.dialog.createTitle')
          : t('manpower.effortMatrix.dialog.versionTitle')
      }
      note={t('manpower.effortMatrix.dialog.versioningNote')}
      autoFocusHeading
      {...(onCloseAutoFocus !== undefined && { onCloseAutoFocus })}
    >
      {blocking.length > 0 && (
        <Alert variant="destructive">
          <AlertTitle>{t('manpower.effortMatrix.dialog.rejectedTitle')}</AlertTitle>
          <AlertDescription>
            <FindingList findings={blocking} />
          </AlertDescription>
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert className="border-[var(--warn)]">
          <AlertTitle>{t('manpower.effortMatrix.dialog.confirmTitle')}</AlertTitle>
          <AlertDescription>
            <FindingList findings={warnings} />
          </AlertDescription>
        </Alert>
      )}

      {/*
        Two fields per row, paired by what a reader compares rather than by
        size: the combination R2.1 keys the assumption by (activity type +
        role, then population unit) on the first two rows, and the two
        figures (effort per unit + fixed overhead) on the last.

        `mb-0` on every field is load-bearing: `Field` ships `mb-3` of its
        own, and with the grid's `gap-y-3` that spacing applied twice.
      */}
      <div className="grid grid-cols-2 items-start gap-x-3 gap-y-3">
        <Field
          label={t('manpower.effortMatrix.activityType')}
          htmlFor="ea-activity"
          required
          requiredMark="*"
          className="mb-0"
        >
          <select
            id="ea-activity"
            className={CONTROL_CLASS}
            value={form.activityTypeId}
            onChange={(event) => set('activityTypeId', event.target.value)}
          >
            <option value="">{t('manpower.effortMatrix.dialog.selectActivityType')}</option>
            {activityTypes.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={t('manpower.effortMatrix.role')}
          htmlFor="ea-role"
          required
          requiredMark="*"
          className="mb-0"
        >
          <select
            id="ea-role"
            className={CONTROL_CLASS}
            value={form.ffRole}
            onChange={(event) => set('ffRole', event.target.value)}
          >
            {FF_ROLE_OPTIONS.map((role) => (
              <option key={role.id} value={role.id}>
                {role.label}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={t('manpower.effortMatrix.populationUnit')}
          htmlFor="ea-population"
          required
          requiredMark="*"
          className="mb-0"
        >
          <select
            id="ea-population"
            className={CONTROL_CLASS}
            value={form.populationUnitId}
            onChange={(event) => set('populationUnitId', event.target.value)}
          >
            <option value="">{t('manpower.effortMatrix.dialog.selectPopulationUnit')}</option>
            {populationUnits.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={t('manpower.effortMatrix.effectiveFrom')}
          htmlFor="ea-effective"
          required
          requiredMark="*"
          className="mb-0"
          hint={t('manpower.effortMatrix.dialog.effectiveFromHint')}
        >
          <DateField
            value={form.effectiveFrom}
            onChange={(date) => set('effectiveFrom', date ?? '')}
            label={t('manpower.effortMatrix.effectiveFrom')}
          />
        </Field>

        <Field
          label={t('manpower.effortMatrix.effortPerUnit')}
          htmlFor="ea-effort"
          required
          requiredMark="*"
          className="mb-0"
          {...(effortOutOfRange
            ? {
                hint: t('manpower.effortMatrix.dialog.effortOutOfRange', {
                  min: EFFORT_PER_UNIT_MINUTES_MIN,
                  max: EFFORT_PER_UNIT_MINUTES_MAX,
                }),
              }
            : { hint: t('manpower.effortMatrix.dialog.effortHint') })}
        >
          <TextInput
            id="ea-effort"
            type="number"
            min={EFFORT_PER_UNIT_MINUTES_MIN}
            max={EFFORT_PER_UNIT_MINUTES_MAX}
            step={0.01}
            className="w-full"
            value={form.effortPerUnitMinutes}
            onChange={(event) => set('effortPerUnitMinutes', event.target.value)}
          />
        </Field>

        <Field
          label={t('manpower.effortMatrix.fixedOverhead')}
          htmlFor="ea-overhead"
          className="mb-0"
          hint={t('manpower.effortMatrix.dialog.fixedOverheadHint')}
        >
          <TextInput
            id="ea-overhead"
            type="number"
            min={FIXED_OVERHEAD_HOURS_MIN}
            max={FIXED_OVERHEAD_HOURS_MAX}
            step={0.01}
            className="w-full"
            value={form.fixedOverheadHours}
            onChange={(event) => set('fixedOverheadHours', event.target.value)}
          />
        </Field>
      </div>

      <EditorDialogActions>
        {warnings.length > 0 ? (
          <Button
            onClick={() => submit(warnings.map((finding) => finding.ruleId))}
            disabled={!submittable}
          >
            {t('manpower.effortMatrix.dialog.confirmAndSave')}
          </Button>
        ) : (
          <Button onClick={() => submit([])} disabled={!submittable}>
            {isSubmitting ? t('admin.common.saving') : t('admin.common.save')}
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={() => onOpenChange(false)}
          disabled={isSubmitting}
        >
          {t('admin.common.cancel')}
        </Button>
      </EditorDialogActions>
    </EditorDialog>
  );
}

/**
 * The minute and hour columns are `numeric(_,2)` and the contracts carry them as
 * text to avoid float drift, so a typed `3` is sent as `3.00`.
 */
function toDecimalText(value: string): EffortAssumptionWrite['effortPerUnitMinutes'] {
  const numeric = Number(value);
  const text = Number.isFinite(numeric) ? numeric.toFixed(2) : value;
  return text as EffortAssumptionWrite['effortPerUnitMinutes'];
}
